import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../../config/api';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ loginUser, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ loginUser, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'ログインに失敗しました');
      }
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return {
        user: data.user,
        token: data.token,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  return null;
});

export const sendForgotPasswordEmail = createAsyncThunk(
  'auth/sendForgotPasswordEmail',
  async (email, { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'メール送信に失敗しました');
      }
      return { email: data.email, message: data.message || 'パスコードをメールで送信しました' };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const verifyPasscode = createAsyncThunk(
  'auth/verifyPasscode',
  async ({ email, passcode }, { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.VERIFY_PASSCODE, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, passcode }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'パスコードが正しくありません');
      }

      return { email: data.email, message: data.message || 'パスコードが確認されました' };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, newPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue('パスワードの変更に失敗しました');
      }

      return { message: data.message || 'パスワードが正常に変更されました' };
    } catch (error) {
      return rejectWithValue('ネットワークエラーが発生しました');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldpassword, newpassword, newpassword_confirmation }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldpassword,
          newpassword,
          newpassword_confirmation,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'パスワードの変更に失敗しました');
      }

      return { message: data.message || 'パスワードが正常に変更されました' };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async ({ cmLoginId, cmNameStaff, cmTel }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth.token;
      const currentUser = auth.user;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      if (!currentUser || !currentUser.cmId) {
        return rejectWithValue('ユーザー情報が見つかりません');
      }

      const response = await fetch(API_ENDPOINTS.PROFILE(currentUser.cmId), {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cmLoginId,
          cmNameStaff,
          cmTel,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return rejectWithValue(data.message || 'プロフィールの更新に失敗しました');
      }

      const updatedUser = data.user
        ? data.user
        : {
            ...currentUser,
            cmLoginId,
            cmNameStaff,
            cmTel,
          };

      localStorage.setItem('user', JSON.stringify(updatedUser));

      return {
        message: data.message || 'プロフィールが正常に更新されました',
        user: updatedUser,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

const getStoredUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

const initialState = {
  user: getStoredUser(),
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
  forgotPassword: {
    isLoading: false,
    isEmailSent: false,
    isPasscodeVerified: false,
    isPasswordReset: false,
    email: '',
    error: null,
    message: null,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetForgotPassword: (state) => {
      state.forgotPassword = {
        isLoading: false,
        isEmailSent: false,
        isPasscodeVerified: false,
        isPasswordReset: false,
        email: '',
        error: null,
        message: null,
      };
    },
    clearForgotPasswordError: (state) => {
      state.forgotPassword.error = null;
      state.forgotPassword.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(sendForgotPasswordEmail.pending, (state) => {
        state.forgotPassword.isLoading = true;
        state.forgotPassword.error = null;
        state.forgotPassword.message = null;
      })
      .addCase(sendForgotPasswordEmail.fulfilled, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.isEmailSent = true;
        state.forgotPassword.email = action.payload.email;
        state.forgotPassword.message = action.payload.message;
      })
      .addCase(sendForgotPasswordEmail.rejected, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.error = action.payload;
      })
      .addCase(verifyPasscode.pending, (state) => {
        state.forgotPassword.isLoading = true;
        state.forgotPassword.error = null;
        state.forgotPassword.message = null;
      })
      .addCase(verifyPasscode.fulfilled, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.isPasscodeVerified = true;
        state.forgotPassword.message = action.payload.message;
      })
      .addCase(verifyPasscode.rejected, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.forgotPassword.isLoading = true;
        state.forgotPassword.error = null;
        state.forgotPassword.message = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.isPasswordReset = true;
        state.forgotPassword.message = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.forgotPassword.isLoading = false;
        state.forgotPassword.error = action.payload;
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetForgotPassword, clearForgotPasswordError } = authSlice.actions;
export default authSlice.reducer;
